// @-Erwähnungs-Helfer (CR-SP3) — aus NoteChat extrahiert, damit auch das
// Vorgang-Erstell-Formular (MentionTextarea) dieselbe Logik nutzt.
// REINE Funktionen, vitest-getestet (mentions.test.ts).

import { userDisplay, type AppUser } from "../../services/userService";

/** Laufendes Trigger-`token` (@ oder #) unmittelbar vor dem Cursor. */
const activeToken = (
    text: string,
    caret: number,
    trigger: "@" | "#",
): { start: number; query: string } | null => {
    const upto = text.slice(0, caret);
    const at = upto.lastIndexOf(trigger);
    if (at < 0) return null;
    const between = upto.slice(at + 1);
    // Kein Whitespace zwischen Trigger und Cursor (Token endet am ersten Leerzeichen).
    if (/\s/.test(between)) return null;
    return { start: at, query: between };
};

/** Laufendes `@token` unmittelbar vor dem Cursor (kein Whitespace dazwischen). */
export const activeMention = (
    text: string,
    caret: number,
): { start: number; query: string } | null => activeToken(text, caret, "@");

export type PersonTrigger = "@" | "#";
export type PersonSelection = { userId: string; trigger: PersonTrigger };

/**
 * Laufendes Personen-Token. `#` wird nur in den dafür freigeschalteten
 * Vorgangs-Eingaben als „zu informieren" interpretiert; andernorts bleibt die
 * bestehende Hashtag-/To-do-Logik unangetastet.
 */
export const activePersonToken = (
    text: string,
    caret: number,
    hashEnabled = false,
): { start: number; query: string; trigger: PersonTrigger } | null => {
    const at = activeToken(text, caret, "@");
    const hash = hashEnabled ? activeToken(text, caret, "#") : null;
    if (!at && !hash) return null;
    if (!hash || (at && at.start > hash.start)) return at ? { ...at, trigger: "@" } : null;
    return { ...hash, trigger: "#" };
};

/**
 * Laufendes `#token` unmittelbar vor dem Cursor (SP10) — für die To-do-Auswahl
 * per Hashtag im Notiz-Chat. Die Query ist der Text bis zum ersten Leerzeichen;
 * To-do-Betreffe mit Leerzeichen werden über den ersten Wortteil gefiltert.
 */
export const activeHashtag = (
    text: string,
    caret: number,
): { start: number; query: string } | null => activeToken(text, caret, "#");

/** Text-Token einer Erwähnung („@<token> ") — Username bzw. E-Mail-Lokalteil. */
export const mentionToken = (u: AppUser): string =>
    u.username || u.email?.split("@")[0] || userDisplay(u);

/**
 * Löst getippte @token aus dem Rohtext gegen die Userliste auf (Username bzw.
 * E-Mail-Lokalteil, z.B. „@dc" → dc@courte.com) — damit eine Erwähnung auch
 * ohne Dropdown-Auswahl zählt.
 */
export const resolveTypedMentions = (body: string, users: AppUser[]): string[] => {
    const ids: string[] = [];
    const re = /@([\w.-]+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(body)) !== null) {
        const tok = m[1].toLowerCase();
        const u = users.find(
            (x) =>
                (x.username ?? "").toLowerCase() === tok ||
                (x.email ?? "").split("@")[0].toLowerCase() === tok ||
                mentionToken(x).toLowerCase() === tok,
        );
        if (u) ids.push(u.id);
    }
    return ids;
};

/** Minimal-Form eines To-dos für die #-Auswahl. */
export type HashtagTodo = { id: number; titel: string };

/**
 * Filtert die To-dos eines Vorgangs für die #-Autocomplete (SP10): Betreff
 * enthält die (case-insensitiv getrimmte) Query; leere Query = alle. Reihenfolge
 * bleibt stabil, das Ergebnis wird auf `limit` gekappt.
 */
export const filterTodosByQuery = (
    todos: HashtagTodo[],
    query: string,
    limit = 8,
): HashtagTodo[] => {
    const q = query.trim().toLowerCase();
    const matches = q ? todos.filter((t) => (t.titel ?? "").toLowerCase().includes(q)) : todos;
    return matches.slice(0, limit);
};

/**
 * Effektive Erwähnungen eines Textes: per Dropdown gewählte Ids, deren Token
 * noch im Text steht, PLUS frei getippte @token — dedupliziert. Spiegelt die
 * Submit-Logik des NoteChat-Composers.
 */
export const effectiveMentions = (
    body: string,
    pickedIds: string[],
    users: AppUser[],
): string[] => {
    const picked = pickedIds.filter((id) => {
        const u = users.find((x) => x.id === id);
        return u && body.includes(`@${mentionToken(u)}`);
    });
    return Array.from(new Set([...picked, ...resolveTypedMentions(body, users)]));
};

/**
 * Wirksame Personenauswahl einer Vorgangs-Notiz. Eine per `#` ausgewählte
 * Person ist zugleich erwähnt und „zu informieren"; gelöschte Tokens fallen
 * wieder aus beiden Listen.
 */
export const effectivePersonSelection = (
    body: string,
    picked: PersonSelection[],
    users: AppUser[],
): { mentions: string[]; zuInformieren: string[] } => {
    const vorhandene = picked.filter(({ userId, trigger }) => {
        const user = users.find((candidate) => candidate.id === userId);
        return !!user && body.includes(`${trigger}${mentionToken(user)}`);
    });
    return {
        mentions: Array.from(
            new Set([
                ...vorhandene.map(({ userId }) => userId),
                ...resolveTypedMentions(body, users),
            ]),
        ),
        zuInformieren: Array.from(
            new Set(
                vorhandene
                    .filter(({ trigger }) => trigger === "#")
                    .map(({ userId }) => userId),
            ),
        ),
    };
};
